import { X } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAddUsageCredits, useUserCreditWallet } from '@/hooks/useUsers';

export default function AddUsageCreditsModal({
  isOpen,
  onClose,
  userId,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const addUsageCredits = useAddUsageCredits();
  const toast = useToast();
  const { data: wallet } = useUserCreditWallet(userId);

  if (!isOpen) return null;

  const dollars = parseFloat(amount);
  const rateCentsPer1000 = wallet?.rate_cents_per_1000 ?? null;
  const creditsPreview =
    rateCentsPer1000 != null && Number.isFinite(dollars) && dollars > 0
      ? Math.floor((Math.round(dollars * 100) * 1000) / rateCentsPer1000)
      : null;

  const handleClose = () => {
    setAmount('');
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!Number.isFinite(dollars) || dollars <= 0)
      return toast.error('Invalid amount', 'Enter a positive dollar value');
    try {
      const res = await addUsageCredits.mutateAsync({
        userId,
        amountUsd: dollars,
        reason: reason || undefined,
      });
      toast.success(
        'Usage credits added',
        `Purchased balance: ${res.purchased_balance} (total ${res.balance})`
      );
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to add usage credits',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Add Usage Credits</CardTitle>
              <CardDescription>
                Convert a dollar value into purchased credits. These never
                expire and are spent last (ledger source: purchase,
                admin-tagged).
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Dollar value
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5.00"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            {creditsPreview !== null && (
              <p className="text-xs text-muted-foreground mt-1">
                = {creditsPreview.toLocaleString()} credits → purchased balance
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for adjustment"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={addUsageCredits.isPending || !amount}
              className="flex-1"
            >
              {addUsageCredits.isPending ? 'Adding...' : 'Add Usage Credits'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
